import Icon, { ICONS } from "@/components/Core/Icons";
import { ProjectCreateLayout } from "@/components/ProjectCreate/layout";
import { cn } from "@/lib/utils";
import { useProjectCreateStore } from "@/store/zustand/projectCreateStore";
import { hide, showNewMessage } from "@intercom/messenger-js-sdk";
import { GitHubLogoIcon } from "@radix-ui/react-icons";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSelector } from "react-redux";
import {
  useIsEmailOnly,
  useIsGitlabConnected,
  useIsGithubConnected,
} from "@/utils/utils";
import { Tooltip } from "react-tooltip";

const WorkflowSelectPage = () => {
  const navigate = useNavigate();
  const projects = useSelector((state: any) => state.supabaseStore.projects);

  const resetProjectCreateFlow = useProjectCreateStore(
    (state) => state.resetProjectCreateFlow
  );

  const isEmailOnly = useIsEmailOnly();
  const isGithubConnected = useIsGithubConnected();
  const isGitlabConnected = useIsGitlabConnected();

  const [selectedWorkflow, setSelectedWorkflow] = useState<string | null>(null);

  return (
    <ProjectCreateLayout showProjectName={false} hideCreateProject={true}>
      <div className="flex flex-col flex-1 justify-center items-center text-md overflow-auto relative">
        {/* Ellipse 17 */}
        {Object.keys(projects).length > 0 && (
          <Button
            variant="outline"
            size="sm"
            style={{
              borderWidth: "1px",
              borderColor: "rgba(47, 48, 53, 1)",
              borderStyle: "solid",
            }}
            className="flex items-center rounded-lg gap-2 self-start"
            onClick={() => navigate("/")}
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        )}
        <div className="absolute w-[350px] h-[350px] left-[calc(50%-175px)] top-[126px] bg-[rgba(75,75,81,0.25)] rounded-full filter blur-[125px] opacity-70 z-[-1]"></div>
        <h1 className="text-[36px] font-medium text-white mb-9">
          What would you like to do?
        </h1>
        <div className="flex justify-center items-center gap-4 w-[800px] mb-9">
          <div
            className={cn(
              "group flex flex-col items-start max-w-[250px] w-full justify-center gap-3 p-4 rounded-lg border border-[#2F3035] cursor-pointer transition-all hover:bg-[#262629]",
              selectedWorkflow === "new" && "border border-[#6448EF]"
            )}
            onClick={() => {
              setSelectedWorkflow("new");
              navigate("/project-create/ai-new");
              resetProjectCreateFlow();
              hide();
            }}
          >
            <div className="p-1 rounded-md bg-[#2A2A2A] group-hover:bg-[#333333] transition-colors">
              <Icon icon={ICONS.AI_LOGO} className="w-4 h-4" />
            </div>
            <div className="flex flex-col items-start text-left">
              <span className="text-[16px] text-white font-medium">
                Generate an app
              </span>
              <p className="text-[12px] text-white/60 mt-1">
                Describe your idea and Tempo will bring it to life
              </p>
            </div>
          </div>

          <div
            className={cn(
              "group flex flex-col items-start max-w-[250px] w-full justify-center gap-3 p-4 rounded-lg border border-[#2F3035] transition-all",
              selectedWorkflow === "github" && "border border-[#6448EF]",
              isEmailOnly || !isGithubConnected
                ? "cursor-not-allowed opacity-45"
                : "cursor-pointer opacity-100"
            )}
            data-tooltip-id="select-tooltip"
            data-tooltip-content={
              isEmailOnly || !isGithubConnected
                ? "Connect Github in User Settings from your profile dropdown"
                : ""
            }
            onClick={() => {
              if (!isGithubConnected) return;
              setSelectedWorkflow("github");
              navigate("/project-create/existing-project");
            }}
          >
            <div className="p-1 rounded-md bg-[#2A2A2A] group-hover:bg-[#333333] transition-colors">
              <GitHubLogoIcon className="w-4 h-4" />
            </div>
            <div className="flex flex-col items-start text-left">
              <span className="text-[16px] text-white font-medium">
                Connect Github Repository
              </span>
              <p className="text-[12px] text-white/60 mt-1">
                Easily deploy projects, sync code, and track changes
              </p>
            </div>
          </div>

          <div
            className={cn(
              "group flex flex-col items-start max-w-[250px] w-full justify-center gap-3 p-4 rounded-lg border border-[#2F3035] transition-all",
              selectedWorkflow === "gitlab" && "border border-[#6448EF]",
              isEmailOnly || !isGitlabConnected
                ? "cursor-not-allowed opacity-45"
                : "cursor-pointer opacity-100"
            )}
            data-tooltip-id="select-tooltip"
            data-tooltip-content={
              isEmailOnly || !isGitlabConnected
                ? "Connect Gitlab in User Settings from your profile dropdown"
                : ""
            }
            onClick={() => {
              if (!isGitlabConnected) return;
              setSelectedWorkflow("gitlab");
              navigate("/project-create/existing-project?provider=gitlab");
            }}
          >
            <div className="p-1 rounded-md bg-[#2A2A2A] group-hover:bg-[#333333] transition-colors">
              <img src="/icons/gitlab-logo.svg" className="w-4 h-4" />
            </div>
            <div className="flex flex-col items-start text-left">
              <span className="text-[16px] text-white font-medium">
                Connect Gitlab Repository
              </span>
              <p className="text-[12px] text-white/60 mt-1">
                Easily deploy projects, sync code, and track changes
              </p>
            </div>
          </div>
        </div>
      </div>
      <Tooltip id="select-tooltip" place="bottom" />
    </ProjectCreateLayout>
  );
};

export default WorkflowSelectPage;
